import { Box, Button, Card, Divider, Stack, ClickAwayListener, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parse } from 'date-fns';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useTranslation } from 'react-i18next';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LockClockOutlined from '@mui/icons-material/LockClockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useFlowMode } from '../use-flow-mode';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { dateToMinutesFromMidnight } from '@/helpers/date.helper';
import TimePicker from '@/components/common/time-picker';
import { calculateFlowDurationFromSlots } from './calculate';
import { isUntimedSlot } from './slot-timing';

function timedSessionsAndBreaks(slots: FlowSlotDto[] | undefined | null): FlowSlotDto[] {
  if (!slots?.length) return [];
  return slots.filter((s) => s.contentType !== 'Gap' && !isUntimedSlot(s));
}

type InlineEndTimingFormProps = {
  flow: FlowDto;
  onClose: () => void;
  onPreviewUpdate?: (updatedFlow: FlowDto) => void;
  cancelChanges?: () => void;
  onSubmit?: (updatedFlow: FlowDto) => void;
  eventSlotIdParam?: string | null;
};

const endTimingSchema = z.object({
  endTime: z.string().min(1, 'End time is required'),
});

type EndTimingFormType = z.infer<typeof endTimingSchema>;

/** Nearest ancestor that scrolls (the slot list lives in a Box with overflowY: auto, not document.body). */
function getScrollParent(node: HTMLElement | null): HTMLElement | null {
  if (!node) return null;
  let p: HTMLElement | null = node.parentElement;
  while (p) {
    const { overflowY, overflowX } = window.getComputedStyle(p);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
      return p;
    }
    p = p.parentElement;
  }
  return null;
}

export default function InlineEndTimingForm({
  flow,
  previewFlow,
  localSlots,
  onClose,
  onPreviewUpdate,
  cancelChanges,
  onSubmit: onSubmitParent,
  eventSlotIdParam,
}: InlineEndTimingFormProps & { previewFlow?: FlowDto; localSlots?: FlowSlotDto[] }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mode = useFlowMode();
  // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
  const flowDurationPlannedMinutes = flow?.durationPlannedMinutes;
  const [showFixedEndTime, setShowFixedEndTime] = useState(
    flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0,
  );
  const [initialShowFixedEndTime, setInitialShowFixedEndTime] = useState(
    () => flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0,
  );
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const isResettingRef = useRef(false);
  const isUpdatingPreviewRef = useRef(false);
  const didAcceptRef = useRef(false);
  const onPreviewUpdateRef = useRef(onPreviewUpdate);
  const flowIdRef = useRef(flow?.id);
  const flowVersionRef = useRef(flow?.version);
  const endTimingFormRef = useRef<HTMLDivElement | null>(null);

  // Keep ref updated
  useEffect(() => {
    onPreviewUpdateRef.current = onPreviewUpdate;
  }, [onPreviewUpdate]);

  // Mobile: desktop-mode TimePicker uses a Popper, not a full-screen dialog. The slot list scrolls
  // inside a nested overflow: auto container (flow detail), not only on body — locking html/body alone
  // does not stop that scroller on iOS. Lock the nearest scroll parent + document while the picker is open.
  useEffect(() => {
    if (!isTimePickerOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const scrollParent = getScrollParent(endTimingFormRef.current);
    const prevScrollOverflow = scrollParent?.style.overflow;
    const prevOverscroll = scrollParent?.style.overscrollBehavior;
    if (scrollParent) {
      scrollParent.style.overflow = 'hidden';
      scrollParent.style.overscrollBehavior = 'none';
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (scrollParent) {
        scrollParent.style.overflow = prevScrollOverflow ?? '';
        scrollParent.style.overscrollBehavior = prevOverscroll ?? '';
      }
    };
  }, [isTimePickerOpen]);

  // Track flow ID/version changes (actual flow changes, not preview updates)
  useEffect(() => {
    if (flow?.id !== flowIdRef.current || flow?.version !== flowVersionRef.current) {
      flowIdRef.current = flow?.id;
      flowVersionRef.current = flow?.version;
    }
  }, [flow?.id, flow?.version]);

  // Use preview flow if available for live preview, otherwise use actual flow
  const flowToUse = previewFlow ?? flow;
  // For master flows (no eventSlotIdParam), always use 0 as start offset (offset from 00:00)
  // For eventSlot-specific flows, use slot.startOffsetMinutes if available
  const flowStartOffset = eventSlotIdParam ? (flowToUse?.slot?.startOffsetMinutes ?? 0) : 0;
  // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
  const flowDuration = flowToUse?.durationPlannedMinutes;
  // Use localSlots if available (reflects current preview state), otherwise use flowToUse.slots
  const slotsToUse = localSlots && localSlots.length > 0 ? localSlots : flowToUse?.slots;
  const timedSlotsForEndPreview = timedSessionsAndBreaks(slotsToUse);
  const calculatedDurationFromTimed =
    timedSlotsForEndPreview.length > 0 ? calculateFlowDurationFromSlots(timedSlotsForEndPreview) : undefined;
  const currentFlowDuration = flowDuration ?? calculatedDurationFromTimed ?? 0;
  // Match slot list: with no timed sessions/breaks, preview end = flow start (+0), not end-of-day (23:59).
  const flowEndOffset =
    currentFlowDuration > 0
      ? flowStartOffset + currentFlowDuration
      : timedSlotsForEndPreview.length === 0
        ? flowStartOffset
        : flowStartOffset + (calculatedDurationFromTimed ?? 0);
  const endTime = minuteOffsetToTime(flowEndOffset);

  const {
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<EndTimingFormType>({
    resolver: zodResolver(endTimingSchema),
    defaultValues: {
      endTime: endTime,
    },
  });

  // Reset form only when committed `flow` changes — not when preview (`previewFlow` / `localSlots`) updates.
  // Otherwise picking a fixed end time updates preview → this effect ran → reset() cleared isDirty and
  // synced initialShowFixedEndTime with showFixedEndTime, disabling Confirm (flicker).
  useEffect(() => {
    if (isResettingRef.current) return;

    const fd = flow?.durationPlannedMinutes;
    const slotsToUseForReset = flow?.slots;
    const timedForReset = timedSessionsAndBreaks(slotsToUseForReset);
    const calculatedDur = timedForReset.length > 0 ? calculateFlowDurationFromSlots(timedForReset) : undefined;
    const curDur = fd ?? calculatedDur ?? 0;
    const startOff = eventSlotIdParam ? (flow?.slot?.startOffsetMinutes ?? 0) : 0;
    const newEndOffset =
      curDur > 0 ? startOff + curDur : timedForReset.length === 0 ? startOff : startOff + (calculatedDur ?? 0);
    const newEndTime = minuteOffsetToTime(newEndOffset);
    reset({
      endTime: newEndTime,
    });
    queueMicrotask(() => {
      const fixed = fd !== undefined && fd > 0;
      setShowFixedEndTime(fixed);
      setInitialShowFixedEndTime(fixed);
    });
  }, [
    flow?.id,
    flow?.version,
    flow?.durationPlannedMinutes,
    flow?.slot?.startOffsetMinutes,
    flow?.slots,
    eventSlotIdParam,
    reset,
  ]);

  const endTimeValue = useWatch({ control, name: 'endTime' });

  /** Stable reference for MUI TimePicker `value` — avoid new Date() each render resetting the wheel. */
  const endTimeDateValue = useMemo(
    () => (endTimeValue ? parse(endTimeValue, 'HH:mm', new Date()) : null),
    [endTimeValue],
  );

  const hasUnsavedChanges = isDirty || showFixedEndTime !== initialShowFixedEndTime;

  // Update preview when form values change (not while the time picker is open — wheel onChange would
  // fire preview updates every tick, re-render the tree, and fight MUI's internal wheel state).
  useEffect(() => {
    if (!onPreviewUpdateRef.current || !flow || isResettingRef.current || isUpdatingPreviewRef.current) return;
    if (isTimePickerOpen) return;

    const baseFlow = previewFlow ?? flow;

    // Calculate flowEndOffset locally to avoid dependency issues
    // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
    const localFlowDuration = baseFlow?.durationPlannedMinutes;
    const localSlotsToUse = localSlots && localSlots.length > 0 ? localSlots : baseFlow?.slots;
    const timedForPreview = timedSessionsAndBreaks(localSlotsToUse);
    const calculatedDurationFromTimed =
      timedForPreview.length > 0 ? calculateFlowDurationFromSlots(timedForPreview) : undefined;
    const currentFlowDuration = localFlowDuration ?? calculatedDurationFromTimed ?? 0;
    const localFlowEndOffset =
      currentFlowDuration > 0
        ? flowStartOffset + currentFlowDuration
        : timedForPreview.length === 0
          ? flowStartOffset
          : flowStartOffset + (calculatedDurationFromTimed ?? 0);

    if (showFixedEndTime) {
      // When fixed end-time is enabled, update preview with the form value
      const endMinutes = endTimeValue ? dateToMinutesFromMidnight(endTimeValue) : localFlowEndOffset;
      const durationMinutes = endMinutes - flowStartOffset;

      if (durationMinutes > 0) {
        const updatedFlow: FlowDto = {
          ...baseFlow,
          durationPlannedMinutes: durationMinutes,
          slot: baseFlow.slot
            ? {
                ...baseFlow.slot,
                startOffsetMinutes: flowStartOffset,
                durationPlannedMinutes: durationMinutes,
              }
            : undefined,
        };

        // Use setTimeout to avoid calling during render
        isUpdatingPreviewRef.current = true;
        setTimeout(() => {
          onPreviewUpdateRef.current?.(updatedFlow);
          isUpdatingPreviewRef.current = false;
        }, 0);
      }
    } else {
      // When fixed end-time is disabled, remove durationPlannedMinutes from both flow and slot
      const updatedFlow: FlowDto = {
        ...baseFlow,
        durationPlannedMinutes: undefined,
        slot: baseFlow.slot
          ? {
              ...baseFlow.slot,
              durationPlannedMinutes: undefined,
            }
          : undefined,
      };

      isUpdatingPreviewRef.current = true;
      setTimeout(() => {
        onPreviewUpdateRef.current?.(updatedFlow);
        isUpdatingPreviewRef.current = false;
      }, 0);
    }
  }, [endTimeValue, showFixedEndTime, flowStartOffset, previewFlow, flow, localSlots, isTimePickerOpen]);

  const handleConfirm = useCallback(() => {
    if (!hasUnsavedChanges || !flowToUse) return;

    if (!showFixedEndTime) {
      const updatedFlow: FlowDto = {
        ...flowToUse,
        durationPlannedMinutes: undefined,
        slot: flowToUse.slot ? { ...flowToUse.slot, durationPlannedMinutes: undefined } : undefined,
      };
      onSubmitParent?.(updatedFlow);
    } else {
      const endMinutes = dateToMinutesFromMidnight(endTimeValue);
      const durationMinutes = endMinutes - flowStartOffset;
      if (durationMinutes <= 0) return;

      const updatedFlow: FlowDto = {
        ...flowToUse,
        durationPlannedMinutes: durationMinutes,
        slot: flowToUse.slot
          ? {
              ...flowToUse.slot,
              startOffsetMinutes: flowStartOffset,
              durationPlannedMinutes: durationMinutes,
            }
          : undefined,
      };
      onSubmitParent?.(updatedFlow);
    }
    onClose();
  }, [hasUnsavedChanges, flowToUse, showFixedEndTime, endTimeValue, flowStartOffset, onSubmitParent, onClose]);

  const handleCancel = useCallback(() => {
    isResettingRef.current = true;
    const slotsToUseForReset = flow?.slots;
    const fd = flow?.durationPlannedMinutes;
    const timedForCancel = timedSessionsAndBreaks(slotsToUseForReset);
    const calculatedDur = timedForCancel.length > 0 ? calculateFlowDurationFromSlots(timedForCancel) : undefined;
    const curDur = fd ?? calculatedDur ?? 0;
    const newEndOffset =
      curDur > 0
        ? flowStartOffset + curDur
        : timedForCancel.length === 0
          ? flowStartOffset
          : flowStartOffset + (calculatedDur ?? 0);
    const newEndTime = minuteOffsetToTime(newEndOffset);
    reset({ endTime: newEndTime });
    queueMicrotask(() => {
      const fixed = fd !== undefined && fd > 0;
      setShowFixedEndTime(fixed);
      setInitialShowFixedEndTime(fixed);
      setIsTimePickerOpen(false);
      isResettingRef.current = false;
    });
    if (onPreviewUpdateRef.current && flow) {
      isUpdatingPreviewRef.current = true;
      setTimeout(() => {
        onPreviewUpdateRef.current?.(flow);
        isUpdatingPreviewRef.current = false;
      }, 0);
    }
    cancelChanges?.();
    onClose();
  }, [flow, flowStartOffset, reset, cancelChanges, onClose]);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      const isTouchEvent = 'touches' in event || 'changedTouches' in event;

      // Don't close if clicking within the end-timing form itself
      if (target.closest('[data-end-timing-form]')) {
        return;
      }

      // Check if clicking on a slot
      const path = (event as Event & { composedPath?: () => EventTarget[] })?.composedPath?.();
      const candidates: (HTMLElement | EventTarget)[] = [];

      if (path && Array.isArray(path)) candidates.push(...path);
      candidates.push(target);

      // Walk up the DOM as a fallback
      let el: HTMLElement | null = target;
      while (el) {
        candidates.push(el);
        el = el.parentElement;
      }

      // Filter to only HTMLElement nodes
      const htmlElements = candidates.filter((node): node is HTMLElement => node instanceof HTMLElement);

      // Check if clicking on a slot (but not the end-timing item itself)
      let clickedSlotId: string | null = null;
      let clickedSlotCard: HTMLElement | null = null;

      for (const node of htmlElements) {
        // Skip if this is the end-timing item itself
        if (node.getAttribute('data-start-end-item')) {
          continue;
        }

        const hasSlotId =
          node.getAttribute('data-slot-id') ||
          node.getAttribute('data-flow-slot-id') ||
          node.getAttribute('data-slotid') ||
          node.getAttribute('data-flow-slotid');

        if (hasSlotId) {
          clickedSlotId = hasSlotId;
          clickedSlotCard = node;
          break;
        }
      }

      // Also check using closest as a fallback
      if (!clickedSlotId) {
        const closestSlot = target.closest('[data-slot-id],[data-flow-slot-id],[data-slotid],[data-flow-slotid]');
        if (closestSlot && !closestSlot.getAttribute('data-start-end-item')) {
          clickedSlotId =
            closestSlot.getAttribute('data-slot-id') ||
            closestSlot.getAttribute('data-flow-slot-id') ||
            closestSlot.getAttribute('data-slotid') ||
            closestSlot.getAttribute('data-flow-slotid') ||
            null;
          clickedSlotCard = closestSlot as HTMLElement;
        }
      }

      // If clicking on a slot, close the form with a delay to allow the slot's onClick handler to execute first
      if (clickedSlotId && clickedSlotCard) {
        const delay = isTouchEvent ? 50 : 0;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              handleCancel();
            }, delay);
          });
        });
        return;
      }

      handleCancel();
    },
    [handleCancel],
  );

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsTimePickerOpen(false);
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleCancel]);

  const handleToggleFixedEndTime = useCallback(() => {
    if (isTimePickerOpen) {
      setIsTimePickerOpen(false);
    }

    const newShowFixed = !showFixedEndTime;
    setShowFixedEndTime(newShowFixed);

    if (!newShowFixed) {
      setIsTimePickerOpen(false);
      // Do not set isResettingRef here — it blocks the preview effect while true, so clearing
      // fixed end never updates preview until confirm. handleCancel still uses isResettingRef.
      const calculatedEndOffset =
        calculatedDurationFromTimed !== undefined && calculatedDurationFromTimed > 0
          ? flowStartOffset + calculatedDurationFromTimed
          : timedSlotsForEndPreview.length === 0
            ? flowStartOffset
            : flowStartOffset + (calculatedDurationFromTimed ?? 0);
      const calculatedEndTime = minuteOffsetToTime(calculatedEndOffset);
      reset({
        endTime: calculatedEndTime,
      });
    } else {
      setIsTimePickerOpen(true);
    }
  }, [
    isTimePickerOpen,
    showFixedEndTime,
    calculatedDurationFromTimed,
    flowStartOffset,
    timedSlotsForEndPreview.length,
    reset,
  ]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Card
        ref={endTimingFormRef}
        data-end-timing-form
        elevation={0}
        sx={{
          cursor: 'default',
          bgcolor: alpha(theme.palette.secondary.main, 0.2),
          outlineWidth: '2px',
          outlineColor: 'secondary.main',
          outlineStyle: 'solid',
          overflow: 'hidden',
          height: '40px',
          borderRadius: 0,
        }}
      >
        <Stack direction="row" alignItems="stretch" height="100%">
          {/* Left timing section */}
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: '75px',
              flexShrink: 0,
              alignSelf: 'stretch',
              height: '100%',
              minHeight: 0,
              cursor: 'default',
            }}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={0.25}
              sx={{ height: '100%', width: '100%', minHeight: 0 }}
            >
              {showFixedEndTime ? (
                <Controller
                  name="endTime"
                  control={control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        minHeight: 0,
                      }}
                    >
                      <TimePicker
                        ampm={false}
                        views={['hours', 'minutes']}
                        value={endTimeDateValue}
                        minTime={parse(minuteOffsetToTime(flowStartOffset + 1), 'HH:mm', new Date())}
                        maxTime={parse('23:59', 'HH:mm', new Date())}
                        timeSteps={{ minutes: 1 }}
                        icon={EditOutlined}
                        forceDesktopMode={true}
                        open={isTimePickerOpen}
                        onOpen={() => setIsTimePickerOpen(true)}
                        onChange={(value) => {
                          if (value) {
                            field.onChange(format(value, 'HH:mm'));
                          } else {
                            field.onChange(null);
                          }
                        }}
                        onAccept={(value) => {
                          if (value) {
                            const newTime = format(value, 'HH:mm');
                            didAcceptRef.current = true;
                            field.onChange(newTime);
                            setIsTimePickerOpen(false);
                          }
                        }}
                        onClose={() => {
                          if (didAcceptRef.current) {
                            didAcceptRef.current = false;
                            setIsTimePickerOpen(false);
                            return;
                          }
                          setIsTimePickerOpen(false);
                        }}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [100, 8],
                                },
                              },
                              {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                  boundary: 'viewport',
                                  padding: 8,
                                },
                              },
                              {
                                name: 'flip',
                                enabled: true,
                              },
                            ],
                            placement: 'bottom-start',
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                            },
                          },
                          actionBar: {
                            actions: ['cancel', 'accept'],
                          },
                          textField: {
                            variant: 'standard',
                            readOnly: true,
                            error: !!errors.endTime,
                            helperText: errors.endTime?.message,
                            sx: {
                              'width': '60px',
                              'maxWidth': 'none',
                              '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                height: 'auto',
                                minHeight: 'auto',
                                fontFamily: 'Roboto',
                                lineHeight: 1.0,
                                letterSpacing: '0.033em',
                                padding: 0,
                                margin: 0,
                              },
                              '& .MuiInputBase-input': {
                                py: 0,
                                px: 0,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontFamily: 'Roboto',
                                lineHeight: 1.0,
                                letterSpacing: '0.033em',
                                height: 'auto',
                                minHeight: 'auto',
                                cursor: 'pointer',
                                pointerEvents: 'none',
                              },
                              // Target MUI TimePicker section content (the actual displayed text)
                              '& .MuiPickersInputBase-root': {
                                'fontSize': '0.875rem !important',
                                'fontWeight': '600 !important',
                                'fontFamily': 'Roboto !important',
                                'lineHeight': '1.0 !important',
                                'letterSpacing': '0.033em !important',
                                'gap': 0,
                                '&::before': {
                                  display: 'none',
                                },
                                '&::after': {
                                  display: 'none',
                                },
                                '&:hover::before': {
                                  display: 'none',
                                },
                                '&:hover::after': {
                                  display: 'none',
                                },
                                '&.Mui-focused::before': {
                                  display: 'none',
                                },
                                '&.Mui-focused::after': {
                                  display: 'none',
                                },
                                '& .MuiPickersInputBase-sectionsContainer': {
                                  marginRight: 0,
                                  paddingRight: 0,
                                  paddingBottom: 0,
                                },
                              },
                              '& .MuiPickersSectionList-sectionContent': {
                                fontSize: '0.875rem !important',
                                fontWeight: '600 !important',
                                fontFamily: 'Roboto !important',
                                lineHeight: '1.0 !important',
                                letterSpacing: '0.033em !important',
                                color: 'text.secondary !important',
                              },
                              '& .MuiPickersInputBase-sectionContent': {
                                fontSize: '0.875rem !important',
                                fontWeight: '600 !important',
                                fontFamily: 'Roboto !important',
                                lineHeight: '1.0 !important',
                                letterSpacing: '0.033em !important',
                                color: 'text.secondary !important',
                              },
                              // Style the TimePicker icon to match 14px size and remove spacing
                              '& .MuiInputAdornment-root': {
                                'marginLeft': 0,
                                'marginRight': 0,
                                'paddingLeft': 0,
                                'paddingRight': 0,
                                '& .MuiIconButton-root': {
                                  'padding': '2px',
                                  'margin': 0,
                                  'marginLeft': 0,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '16px !important',
                                    width: '16px !important',
                                    height: '16px !important',
                                  },
                                },
                              },
                              '& .MuiPickersInputAdornment-root': {
                                'marginLeft': 0,
                                'marginRight': 0,
                                'paddingLeft': 0,
                                'paddingRight': 0,
                                '& .MuiIconButton-root': {
                                  'padding': '2px',
                                  'margin': 0,
                                  'marginLeft': 0,
                                  '& .MuiSvgIcon-root': {
                                    fontSize: '14px !important',
                                    width: '14px !important',
                                    height: '14px !important',
                                  },
                                },
                              },
                              // Remove all underline styles
                              '& .MuiInput-underline:before': {
                                display: 'none',
                              },
                              '& .MuiInput-underline:after': {
                                display: 'none',
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                display: 'none',
                              },
                              '& .MuiInput-underline:hover:not(.Mui-disabled):after': {
                                display: 'none',
                              },
                              '&:hover .MuiInput-underline:before': {
                                display: 'none',
                              },
                              '&:hover .MuiInput-underline:after': {
                                display: 'none',
                              },
                              '&:focus .MuiInput-underline:before': {
                                display: 'none',
                              },
                              '&:focus .MuiInput-underline:after': {
                                display: 'none',
                              },
                              '&:focus-within .MuiInput-underline:before': {
                                display: 'none',
                              },
                              '&:focus-within .MuiInput-underline:after': {
                                display: 'none',
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    minHeight: 0,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontFamily: 'Roboto',
                    lineHeight: 1.0,
                    letterSpacing: '0.033em',
                  }}
                >
                  {endTime}
                </Box>
              )}
            </Stack>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Middle section — fixed end + cancel + confirm (same pattern as inline slot timing) */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              p: 1,
              flex: 1,
              minWidth: 0,
              width: '100%',
              alignSelf: 'stretch',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <Tooltip title={t('flows.detail.slotTiming.fixedEndTime')} leaveDelay={0} disableInteractive>
                <Button
                  type="button"
                  fullWidth
                  variant={showFixedEndTime ? 'contained' : 'outlined'}
                  color="secondary"
                  size="small"
                  aria-label={t('flows.detail.slotTiming.fixedEndTime')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFixedEndTime();
                  }}
                  disabled={isOrganizeMode(mode)}
                >
                  <LockClockOutlined fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                color="secondary"
                size="small"
                aria-label={t('common.cancel')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                disabled={isOrganizeMode(mode)}
                sx={{
                  'color': 'action.active',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CancelOutlinedIcon fontSize="small" />
              </Button>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="success"
                size="small"
                aria-label={t('common.confirm')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirm();
                }}
                disabled={isOrganizeMode(mode) || !hasUnsavedChanges}
              >
                <CheckCircleIcon fontSize="small" />
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Card>
    </ClickAwayListener>
  );
}
